import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import Reveal from "@/components/base/Reveal";
import { articles, categories } from "@/mocks/gossip";
import { hotWords } from "@/mocks/site";

const sortOptions = [
  { id: "heat", label: "按热度" },
  { id: "latest", label: "按时间" },
];

export default function HotPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get("cat") ?? "all";
  const keyword = searchParams.get("q") ?? "";
  const [sortBy, setSortBy] = useState("heat");

  const list = useMemo(() => {
    let result = [...articles];
    if (activeCat !== "all") {
      result = result.filter((item) => item.categoryId === activeCat);
    }
    if (keyword.trim()) {
      const kw = keyword.trim().toLowerCase();
      result = result.filter(
        (item) =>
          item.title.toLowerCase().includes(kw) ||
          item.excerpt.toLowerCase().includes(kw) ||
          item.tags.some((tag) => tag.toLowerCase().includes(kw)),
      );
    }
    result.sort((a, b) => (sortBy === "heat" ? b.heat - a.heat : a.date < b.date ? 1 : -1));
    return result;
  }, [activeCat, keyword, sortBy]);

  const activeCategory = categories.find((item) => item.id === activeCat);
  const ranking = [...articles].sort((a, b) => b.heat - a.heat).slice(0, 5);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setSearchParams(next);
  };

  const hotJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "热门大瓜 - 瓜友会网",
      url: "https://jfinger.cn/hot",
      description: "瓜友会网热门大瓜聚合页，按主题分类浏览经核实的爆料稿件与独家消息。",
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://jfinger.cn" },
        { "@type": "ListItem", position: 2, name: "热门大瓜", item: "https://jfinger.cn/hot" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "热门大瓜列表",
      itemListElement: list.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.title,
        url: `https://jfinger.cn/post/${item.id}`,
      })),
    },
  ];

  return (
    <>
      <Seo
        title="热门大瓜 - 瓜友会网 | 按主题浏览爆料稿件与独家消息"
        description="瓜友会网热门大瓜聚合页，涵盖娱乐圈、网红圈、商业内幕、职场爆料、校园瓜与生活八卦六大栏目，可按热度或时间排序浏览经核实的爆料稿件。"
        keywords="瓜友会网热门大瓜,匿名爆料,线索征集,独家消息,瓜料分类"
        path="/hot"
        ogType="website"
        jsonLd={hotJsonLd}
      />
      <SiteHeader />
      <main>
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full px-4 py-9 md:px-6 md:py-12 lg:px-10">
            <Breadcrumb
              items={[{ label: "首页", to: "/" }, { label: "热门大瓜" }]}
            />
            <h1 className="mt-5 font-heading text-3xl font-bold tracking-tight text-foreground-950 md:text-[40px]">
              {activeCategory ? `${activeCategory.name} · 热门大瓜` : "热门大瓜"}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-8 text-foreground-600 md:text-[15px]">
              {activeCategory
                ? activeCategory.desc
                : "这里聚合了瓜友会网经过核实并发布的主要稿件。每一条都附带了来源背景与时间线整理，你可以按主题筛选，也可以直接搜索关键词。"}
            </p>

            <div className="mt-7 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => updateParam("cat", "")}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                    activeCat === "all"
                      ? "bg-foreground-950 text-background-50"
                      : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                  }`}
                >
                  全部栏目
                </button>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => updateParam("cat", category.id)}
                    className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                      activeCat === category.id
                        ? "bg-primary-500 text-background-50"
                        : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-3">
                <div className="relative">
                  <i
                    className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-foreground-400"
                    aria-hidden="true"
                  />
                  <input
                    type="search"
                    value={keyword}
                    onChange={(event) => updateParam("q", event.target.value)}
                    placeholder="搜索稿件关键词"
                    aria-label="搜索稿件关键词"
                    className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200 md:w-56"
                  />
                </div>
                <div className="flex shrink-0 items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
                  {sortOptions.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setSortBy(option.id)}
                      className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                        sortBy === option.id
                          ? "bg-secondary-500 text-background-50"
                          : "text-foreground-600 hover:text-foreground-900"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full px-4 py-12 md:px-6 lg:px-10">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="mb-6 flex items-center justify-between text-sm text-foreground-500">
                <span>
                  共找到 <strong className="text-foreground-900">{list.length}</strong> 篇稿件
                  {keyword ? `，关键词「${keyword}」` : ""}
                </span>
                {(activeCat !== "all" || keyword) ? (
                  <button
                    type="button"
                    onClick={() => setSearchParams(new URLSearchParams())}
                    className="flex items-center gap-1 whitespace-nowrap text-primary-700 transition-colors hover:text-primary-800"
                  >
                    <i className="ri-refresh-line text-sm" aria-hidden="true" />
                    重置筛选
                  </button>
                ) : null}
              </div>

              {list.length === 0 ? (
                <div className="rounded-lg border border-background-200 bg-background-100 px-6 py-16 text-center">
                  <i className="ri-search-eye-line text-4xl text-background-500" aria-hidden="true" />
                  <p className="mt-4 font-heading text-lg font-bold text-foreground-900">暂时没有匹配的稿件</p>
                  <p className="mt-2 text-sm text-foreground-600">
                    换个关键词试试，或者直接把你手里的线索投给我们。
                  </p>
                  <Link
                    to="/submit"
                    className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-send-plane-line text-base" aria-hidden="true" />
                    我要投稿
                  </Link>
                </div>
              ) : (
                <div className="border-t border-background-200">
                  {list.map((article, index) => (
                    <Reveal key={article.id} delay={(index % 4) * 60}>
                      <ArticleCard article={article} variant="horizontal" />
                    </Reveal>
                  ))}
                </div>
              )}
            </div>

            <aside className="lg:col-span-4">
              <div className="sticky top-28 space-y-6">
                <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                  <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                    <i className="ri-fire-line text-xl text-accent-500" aria-hidden="true" />
                    本周热瓜榜
                  </h2>
                  <ol className="mt-4 divide-y divide-background-200">
                    {ranking.map((item, index) => (
                      <li key={item.id} className="group flex items-start gap-3 py-4">
                        <span
                          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded font-heading text-xs font-bold ${
                            index < 3
                              ? "bg-accent-500 text-background-50"
                              : "bg-background-300 text-foreground-700"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <Link
                          to={`/post/${item.id}`}
                          className="line-clamp-2 text-sm leading-6 text-foreground-800 transition-colors group-hover:text-primary-700"
                        >
                          {item.title}
                        </Link>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <h2 className="font-heading text-lg font-bold text-foreground-950">相关标签</h2>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {hotWords.map((word) => (
                      <button
                        key={word}
                        type="button"
                        onClick={() => updateParam("q", word)}
                        className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                      >
                        #{word}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rounded-lg bg-foreground-950 p-6 text-background-100">
                  <h2 className="font-heading text-lg font-bold text-background-50">你手里的料更热</h2>
                  <p className="mt-2 text-sm leading-7 text-background-300">
                    榜单上的每一条，最初都只是某个瓜友的一段描述。匿名投稿通道全天开放。
                  </p>
                  <Link
                    to="/submit"
                    className="mt-5 inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-400"
                  >
                    <i className="ri-send-plane-line text-base" aria-hidden="true" />
                    匿名投稿
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}